'use client';

import React, { useState, useMemo } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import useSWR from 'swr';
import { RefreshCw, Download, ChevronUp, ChevronDown, ChevronsUpDown, Search, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { formatCurrency, formatPct, formatVolume, formatMarketCap, cn } from '@/lib/utils';
import { getMovers, getPublicPollingSettings, getEarnings } from '@/lib/api-client';
import type { Mover, MoversUniverse } from '@/lib/api-client';
import WatchButton from '@/components/watchlist/WatchButton';
import { useFeatures } from '@/context/SiteContext';
import { useMoversUniverse } from '@/hooks/useMoversUniverse';
import TickerLink from '@/components/ticker/TickerLink';
import { TickerPopupProvider } from '@/components/ticker/TickerPopupProvider';
import { useDilutingSymbols } from '@/hooks/useDilutingSymbols';
import { DilutionBadge } from '@/components/stock/DilutionBadge';

type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 25;

const MC_FILTERS = [
  { label: 'All', test: (_mc: number) => true },
  { label: 'Mega (>$200B)',      test: (mc: number) => mc > 200e9 },
  { label: 'Large ($10B–$200B)', test: (mc: number) => mc >= 10e9 && mc <= 200e9 },
  { label: 'Mid ($2B–$10B)',     test: (mc: number) => mc >= 2e9  && mc < 10e9 },
  { label: 'Small (<$2B)',       test: (mc: number) => mc < 2e9 },
];

const PAGE_CONFIGS: Record<string, { title: string; subtitle: string }> = {
  gainers:    { title: 'Top Gainers',       subtitle: 'Stocks with the highest percentage gains today' },
  losers:     { title: 'Top Losers',        subtitle: 'Stocks with the largest percentage declines today' },
  active:     { title: 'Most Active',       subtitle: 'Stocks with the highest trading volume today' },
  // Combined Pre/After-Hours view (issue #224). Both URLs render the
  // same table — whichever extended session was most recent is what
  // shows. A single badge near the title labels the active session;
  // per-row tags were redundant since rows are typically all from the
  // same most-recent sync.
  premarket:  { title: 'Pre/After-Hours Movers', subtitle: 'Extended-hours movers — after-hours 4:00 PM – 8:00 PM ET, pre-market 4:00 AM – 9:30 AM ET. The table holds the most-recent print across the overnight gap.' },
  afterhours: { title: 'Pre/After-Hours Movers', subtitle: 'Extended-hours movers — after-hours 4:00 PM – 8:00 PM ET, pre-market 4:00 AM – 9:30 AM ET. The table holds the most-recent print across the overnight gap.' },
};

const EXTENDED_HOURS_TYPES = new Set(['premarket', 'afterhours']);

// Sector normalization. Different providers (Tiingo, Alpha Vantage,
// Polygon) ship slightly different sector names for the same GICS
// industry. Canonicalize so the sector dropdown surfaces one entry
// per real sector instead of "Communication" + "Communication
// Services" or "Technology" + "Information Technology" side-by-side.
// Keys are lowercased; values are the display label we want.
const SECTOR_ALIASES: Record<string, string> = {
  'communication': 'Communication Services',
  'communication services': 'Communication Services',
  'communications': 'Communication Services',
  'technology': 'Technology',
  'information technology': 'Technology',
  'healthcare': 'Healthcare',
  'health care': 'Healthcare',
  'financial services': 'Financial Services',
  'financials': 'Financial Services',
  'basic materials': 'Basic Materials',
  'materials': 'Basic Materials',
  'consumer cyclical': 'Consumer Cyclical',
  'consumer discretionary': 'Consumer Cyclical',
  'consumer defensive': 'Consumer Defensive',
  'consumer staples': 'Consumer Defensive',
};
function canonicalSector(raw: string): string {
  const key = raw.trim().toLowerCase();
  if (SECTOR_ALIASES[key]) return SECTOR_ALIASES[key];
  // Fall back to Title-Case for anything we haven't explicitly aliased
  return key.replace(/\b\w/g, (c) => c.toUpperCase());
}

// Single nav entry for the combined Pre/After-Hours view. Both URLs
// (/markets/premarket and /markets/afterhours) still work — they
// render the same merged table — so old bookmarks don't break. We
// route the nav button at `afterhours` since that's the dominant
// session by daily duration.
const NAV_TYPES = [
  { key: 'gainers',    label: 'Top Gainers' },
  { key: 'losers',     label: 'Top Losers' },
  { key: 'active',     label: 'Most Active' },
  { key: 'afterhours', label: 'Pre/After-Hours' },
] as const;

// ── CSV export ──────────────────────────────────────────────────────────────────

function exportCSV(data: Mover[], title: string, isExtendedHours: boolean) {
  const headers = isExtendedHours
    ? ['#', 'Ticker', 'Company', 'Last Close', 'Day %', 'Session', 'Ext. Price', 'Ext. %', 'Volume', 'Avg Volume', 'Ext. Volume', 'Market Cap', 'Sector', 'Industry']
    : ['#', 'Ticker', 'Company', 'Price', 'Change %', 'Volume', 'Market Cap', 'Exchange', 'Sector', 'Industry'];
  const rows = data.map((row, i) => isExtendedHours
    ? [
        i + 1,
        row.ticker,
        row.name,
        row.regularClose ?? row.price ?? '',
        row.dayChangePct ?? '',
        row.sessionLabel === 'pre-market' ? 'PM' : row.sessionLabel === 'after-hours' ? 'AH' : '',
        row.ahPrice ?? '',
        row.ahChangePct ?? '',
        row.volume,
        row.avgVolume ?? '',
        row.ahVolume ?? '',
        row.marketCap,
        row.sector ?? '',
        row.industry ?? '',
      ]
    : [
        i + 1,
        row.ticker,
        row.name,
        row.price,
        row.changePct,
        row.volume,
        row.marketCap,
        row.exchange ?? '',
        row.sector ?? '',
        row.industry ?? '',
      ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Sort header ─────────────────────────────────────────────────────────────────

function SortTh({ label, sortKey, active, dir, onSort, align = 'right' }: {
  label: string; sortKey: string; active: string; dir: SortDir;
  onSort: (k: string) => void; align?: 'left' | 'right';
}) {
  const isActive = active === sortKey;
  return (
    <th
      className={cn(
        'px-4 py-2.5 text-xs font-medium text-fg-subtle uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-fg transition-colors',
        align === 'right' ? 'text-right' : 'text-left'
      )}
      onClick={() => onSort(sortKey)}
    >
      <div className={cn('flex items-center gap-1', align === 'right' && 'justify-end')}>
        {label}
        {isActive
          ? (dir === 'asc' ? <ChevronUp size={12} className="text-sky-400" /> : <ChevronDown size={12} className="text-sky-400" />)
          : <ChevronsUpDown size={12} className="text-slate-600" />}
      </div>
    </th>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────────

export default function MarketMoversPage() {
  const params = useParams();
  const type = (params.type as string) ?? 'gainers';
  const config = PAGE_CONFIGS[type] ?? PAGE_CONFIGS.gainers;
  const features = useFeatures();

  // The combined "Pre/After-Hours" pill (both /premarket and /afterhours
  // URLs) promises "whichever extended session was most recent." The
  // backend classifies each extended-hours row by the ET-hour of its last
  // print (04:00–16:00 → pre-market, else after-hours), so requesting a
  // hardcoded session shows only the stale leftover prints from the OTHER
  // session — e.g. during pre-market the after-hours request returns just
  // the handful of rows last updated yesterday evening. Resolve the
  // session from the current ET hour so the active session's movers
  // surface. Recomputed each render (cheap); SWR key picks up the change.
  // Computed inline (not memoized) so it stays correct as the clock crosses
  // the 16:00 ET boundary while the page is open; the next SWR refresh
  // re-reads it via the keyed fetch below.
  let effectiveType = type;
  if (type === 'premarket' || type === 'afterhours') {
    const etHour = Number(
      new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hourCycle: 'h23' }).format(new Date()),
    );
    effectiveType = etHour >= 4 && etHour < 16 ? 'premarket' : 'afterhours';
  }

  // Earlier this page hid the pre-market / after-hours tables outside
  // the matching session because the ticker row's RTH-session quote
  // would overwrite the extended-hours snapshot, making a stale view
  // misleading. Now that the backend reads `extended_price` /
  // `extended_change_pct` directly (separate columns from the
  // regular-session `price`), the last AH/PM print is preserved across
  // session boundaries — surface it as-is rather than gating, so the
  // operator can still see who moved on the most recent close even
  // hours after the bell.

  // Earnings lookup for the "E" badge. Window = past 5 calendar days
  // through next 7 calendar days. One range fetch on mount serves every
  // row in the table; SWR's cache keeps it stable across tab switches
  // (the page component re-mounts only when /markets/[type] navigates).
  // Failures are silent — the badge just doesn't render.
  const earningsWindow = useMemo(() => {
    const fmt = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(d);
    const today = new Date();
    const past = new Date(today); past.setDate(today.getDate() - 5);
    const future = new Date(today); future.setDate(today.getDate() + 7);
    return { from: fmt(past), to: fmt(future), today: fmt(today) };
  }, []);
  const { data: earningsList } = useSWR(
    ['movers-earnings', earningsWindow.from, earningsWindow.to],
    () => getEarnings(earningsWindow.from, earningsWindow.to),
    { revalidateOnFocus: false, dedupingInterval: 60_000 },
  );
  const earningsByTicker = useMemo(() => {
    const m = new Map<string, { date: string; isPast: boolean }>();
    for (const e of earningsList ?? []) {
      const isPast = e.date < earningsWindow.today;
      m.set(e.ticker.toUpperCase(), { date: e.date, isPast });
    }
    return m;
  }, [earningsList, earningsWindow.today]);
  const dilutingSet = useDilutingSymbols();

  const [mcFilter, setMcFilter]         = useState('All');
  const [exchangeFilter, setExchangeFilter] = useState('All');
  const [sectorFilter, setSectorFilter]  = useState('All');
  // Industry filter cascades off the sector filter — the dropdown only
  // surfaces industries that exist in the currently-selected sector (or
  // every industry when sector === 'All'). Reset to 'All' whenever the
  // parent sector flips so an old industry from the prior sector can't
  // silently zero the result set.
  const [industryFilter, setIndustryFilter] = useState('All');
  const [search, setSearch]             = useState('');
  // Default sort key: 'volume' for active, 'ahChangePct' for AH/PM (so
  // the biggest extended-hours mover lands on row 1), and 'changePct'
  // for gainers/losers (daily-change ranking).
  const [sortKey, setSortKey]           = useState(
    type === 'active'
      ? 'volume'
      : (type === 'afterhours' || type === 'premarket')
        ? 'ahChangePct'
        : 'changePct',
  );
  const [sortDir, setSortDir]           = useState<SortDir>('desc');
  const [page, setPage]                 = useState(1);
  // Issue #244 — universe filter (All / SP500 / R1000) shared with the
  // dashboard tiles via localStorage. Defaults to r1000.
  const [universe, setUniverse] = useMoversUniverse();

  const { data: pollingSettings } = useSWR('polling-settings', getPublicPollingSettings, {
    refreshInterval: 0, revalidateOnFocus: false,
  });
  // No explicit limit — the screener returns everything matching, the
  // server-side cap is 5000. R1000 = max 1000 rows, SP500 = max 500.
  // Pre/AH session filter pre-bounds to a few hundred typically. Most
  // Active "All" universe can hit ~7000 rows — that's the worst case
  // for response size (~3.5MB) but acceptable for a desktop dashboard
  // page. Universe + session filters cascade server-side. Existing
  // client-side filters (market cap, exchange, sector, search) still
  // refine on top. Keying SWR on `universe` so changing the pill
  // triggers a refetch.
  const { data: rows = [], isLoading, mutate } = useSWR(
    `movers-${effectiveType}-${universe}`,
    () => getMovers(
      effectiveType as 'gainers' | 'losers' | 'active' | 'premarket' | 'afterhours',
      { universe },
    ),
    { refreshInterval: (pollingSettings?.marketsRefreshSeconds ?? 60) * 1000 }
  );

  // Derived filter options
  const exchanges = useMemo(() => {
    const vals = [...new Set(rows.map(r => r.exchange).filter(Boolean))] as string[];
    return ['All', ...vals.sort()];
  }, [rows]);

  // Sectors come from the price provider with mixed casing AND mixed
  // naming. Tiingo emits "HEALTHCARE", Alpha Vantage emits "Healthcare"
  // (case dupe), and Polygon emits "Communication" where the GICS
  // standard is "Communication Services" (alias dupe). Normalize on
  // both dimensions so the dropdown surfaces one entry per real sector,
  // and the filter matches all source spellings.
  const sectors = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) {
      if (!r.sector) continue;
      const canon = canonicalSector(r.sector);
      if (!map.has(canon.toLowerCase())) map.set(canon.toLowerCase(), canon);
    }
    return ['All', ...[...map.values()].sort()];
  }, [rows]);

  // Industry list — scoped to the currently-selected sector. When sector
  // === 'All' we collapse every distinct industry across rows; otherwise
  // we restrict to industries that exist in the matching sector.
  const industries = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (!r.industry) continue;
      if (sectorFilter !== 'All' && canonicalSector(r.sector ?? '') !== sectorFilter) continue;
      set.add(r.industry);
    }
    return ['All', ...[...set].sort()];
  }, [rows, sectorFilter]);

  const mcFilterObj = MC_FILTERS.find(f => f.label === mcFilter) ?? MC_FILTERS[0];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter(row => {
        if (!mcFilterObj.test(row.marketCap)) return false;
        if (exchangeFilter !== 'All' && row.exchange !== exchangeFilter) return false;
        if (sectorFilter !== 'All' && canonicalSector(row.sector ?? '') !== sectorFilter) return false;
        if (industryFilter !== 'All' && (row.industry ?? '') !== industryFilter) return false;
        if (q && !row.ticker.toLowerCase().includes(q) && !row.name.toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => {
        const av = (a as Record<string, unknown>)[sortKey];
        const bv = (b as Record<string, unknown>)[sortKey];
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        const as_ = String(av ?? '').toLowerCase();
        const bs_ = String(bv ?? '').toLowerCase();
        return sortDir === 'asc' ? as_.localeCompare(bs_) : bs_.localeCompare(as_);
      });
  }, [rows, mcFilterObj, exchangeFilter, sectorFilter, industryFilter, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Dominant session across the result set — drives the single
  // session badge near the table heading. Rows are usually all from
  // the same most-recent extended sync, but we tally just in case
  // a partial sync leaves a mix mid-flight.
  const dominantSession = useMemo<'pre-market' | 'after-hours' | null>(() => {
    if (!EXTENDED_HOURS_TYPES.has(type)) return null;
    let pm = 0;
    let ah = 0;
    for (const r of rows) {
      if (r.sessionLabel === 'pre-market') pm++;
      else if (r.sessionLabel === 'after-hours') ah++;
    }
    if (pm === 0 && ah === 0) return null;
    return pm > ah ? 'pre-market' : 'after-hours';
  }, [rows, type]);

  function handleSort(key: string) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
    setPage(1);
  }

  function resetFilters() {
    setMcFilter('All');
    setExchangeFilter('All');
    setSectorFilter('All');
    setIndustryFilter('All');
    setSearch('');
    setPage(1);
  }

  const selCls = 'bg-card-bg border border-default rounded-lg px-2.5 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-sky-500 transition-colors';

  return (
    <TickerPopupProvider>
    <div className="px-2 py-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-white">{config.title}</h1>
            {dominantSession && (
              <span
                className={cn(
                  'inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] font-semibold uppercase tracking-wide',
                  dominantSession === 'pre-market'
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                    : 'border-purple-500/40 bg-purple-500/10 text-purple-300',
                )}
                title={dominantSession === 'pre-market'
                  ? 'Current data is from the pre-market session (04:00 – 09:30 ET)'
                  : 'Current data is from the after-hours session (16:00 – 20:00 ET)'}
              >
                {dominantSession === 'pre-market' ? 'Pre-Market' : 'After-Hours'}
              </span>
            )}
          </div>
          <p className="text-fg-muted text-sm mt-1">{config.subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {features.exportData ? (
            <button
              onClick={() => exportCSV(filtered, config.title, EXTENDED_HOURS_TYPES.has(type))}
              disabled={!filtered.length}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card-bg border border-default text-xs text-slate-300 hover:text-white hover:border-muted transition-colors disabled:opacity-40"
            >
              <Download size={13} /> Export CSV
            </button>
          ) : (
            <div className="relative group">
              <button disabled className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card-bg border border-default text-xs text-slate-600 cursor-not-allowed">
                <Lock size={11} className="text-amber-500/70" /> Export CSV
              </button>
              <div className="absolute right-0 top-full mt-1.5 w-48 bg-card-bg border border-default rounded-lg px-3 py-2 text-xs text-fg-muted shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                <span className="text-amber-400 font-medium">Pro feature</span> — upgrade to unlock CSV export.
              </div>
            </div>
          )}
          <button
            onClick={() => mutate()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card-bg border border-default text-xs text-slate-300 hover:text-white hover:border-muted transition-colors"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Tab navigation. The Pre/After-Hours pill lights up for either
          URL (/markets/premarket or /markets/afterhours) since both
          render the same combined view. */}
      <div className="flex flex-wrap gap-2 mb-5">
        {NAV_TYPES.map(({ key, label }) => {
          const isActive = key === 'afterhours'
            ? (type === 'afterhours' || type === 'premarket')
            : type === key;
          return (
            <Link
              key={key}
              href={`/markets/${key}`}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-sky-500 text-white'
                  : 'bg-card-bg border border-default text-fg-muted hover:text-white hover:border-muted'
              )}
            >
              {label}
            </Link>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        {/* Universe filter (issue #244) — All / SP500 / R1000.
            Persisted per-user via useMoversUniverse so the dashboard
            tiles + this page share the same selection. */}
        <div className="inline-flex items-center bg-card-bg border border-default rounded-lg p-0.5 text-xs">
          {(['r1000', 'sp500', 'all'] as MoversUniverse[]).map((u) => (
            <button
              key={u}
              type="button"
              onClick={() => { setUniverse(u); setPage(1); }}
              className={cn(
                'px-3 py-1 rounded-md font-medium uppercase tracking-wide transition-colors',
                universe === u
                  ? 'bg-sky-500/20 text-sky-300'
                  : 'text-fg-subtle hover:text-fg-muted',
              )}
              title={
                u === 'r1000' ? 'Russell 1000 — top 1000 US large/mid caps' :
                u === 'sp500' ? 'S&P 500 — top 500 by index weight' :
                'All — full ticker universe (~5K stocks)'
              }
            >
              {u === 'r1000' ? 'R1000' : u === 'sp500' ? 'S&P 500' : 'All'}
            </button>
          ))}
        </div>

        {/* Company search */}
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
          <input
            type="text"
            placeholder="Search company or ticker…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="bg-card-bg border border-default rounded-lg pl-8 pr-3 py-1.5 text-xs text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-sky-500 transition-colors w-52"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-fg-subtle">Market Cap:</label>
          <select value={mcFilter} onChange={e => { setMcFilter(e.target.value); setPage(1); }} className={selCls}>
            {MC_FILTERS.map(f => <option key={f.label}>{f.label}</option>)}
          </select>
        </div>

        {exchanges.length > 2 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-fg-subtle">Exchange:</label>
            <select value={exchangeFilter} onChange={e => { setExchangeFilter(e.target.value); setPage(1); }} className={selCls}>
              {exchanges.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
        )}

        {sectors.length > 2 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-fg-subtle">Sector:</label>
            <select
              value={sectorFilter}
              onChange={e => { setSectorFilter(e.target.value); setIndustryFilter('All'); setPage(1); }}
              className={selCls}
            >
              {sectors.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
        )}

        {industries.length > 2 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-fg-subtle">Industry:</label>
            <select value={industryFilter} onChange={e => { setIndustryFilter(e.target.value); setPage(1); }} className={selCls}>
              {industries.map(f => <option key={f}>{f}</option>)}
            </select>
          </div>
        )}

        {(mcFilter !== 'All' || exchangeFilter !== 'All' || sectorFilter !== 'All' || industryFilter !== 'All' || search) && (
          <button onClick={resetFilters} className="text-xs text-fg-subtle hover:text-fg transition-colors underline">
            Clear filters
          </button>
        )}

        <span className="text-xs text-slate-600 ml-auto">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="bg-card-bg border border-default rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-default bg-card-hover-bg/50">
              <tr>
                <SortTh label="Ticker"   sortKey="ticker"    active={sortKey} dir={sortDir} onSort={handleSort} align="left" />
                <SortTh label="Company"  sortKey="name"      active={sortKey} dir={sortDir} onSort={handleSort} align="left" />
                {EXTENDED_HOURS_TYPES.has(type) ? (
                  <>
                    {/* AH/PM split: Last Close ($) + Day % is the regular-
                        session summary; After Market ($) + AH % is the
                        extended-hours-specific move. Sort default lands on
                        ahChangePct so the biggest extended movers surface
                        at the top regardless of which way the regular
                        session went. */}
                    <SortTh label="Last Close" sortKey="regularClose" active={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Day %"      sortKey="dayChangePct" active={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Ext. Price" sortKey="ahPrice"     active={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Ext. %"     sortKey="ahChangePct" active={sortKey} dir={sortDir} onSort={handleSort} />
                  </>
                ) : (
                  <>
                    <SortTh label="Price"    sortKey="price"     active={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="Change %" sortKey="changePct" active={sortKey} dir={sortDir} onSort={handleSort} />
                  </>
                )}
                <SortTh label="Volume"   sortKey="volume"    active={sortKey} dir={sortDir} onSort={handleSort} />
                {EXTENDED_HOURS_TYPES.has(type) && (
                  <>
                    <SortTh label="Avg Vol" sortKey="avgVolume" active={sortKey} dir={sortDir} onSort={handleSort} />
                    <SortTh label="AH Vol"  sortKey="ahVolume"  active={sortKey} dir={sortDir} onSort={handleSort} />
                  </>
                )}
                <SortTh label="Mkt Cap"  sortKey="marketCap" active={sortKey} dir={sortDir} onSort={handleSort} />
                {/* Exchange column dropped for AH/PM — operator scanning
                    after-hours movers cares about the price action, not
                    NYSE-vs-NASDAQ. Daily endpoints keep it. */}
                {!EXTENDED_HOURS_TYPES.has(type) && (
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-fg-subtle uppercase tracking-wide hidden md:table-cell">Exchange</th>
                )}
                <th className="px-4 py-2.5 text-left text-xs font-medium text-fg-subtle uppercase tracking-wide hidden lg:table-cell">Sector</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-fg-subtle uppercase tracking-wide hidden xl:table-cell">Industry</th>
                <th className="w-8 px-2 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-default/50">
              {(() => {
                // colspan = Ticker + Company + price/change cols +
                // Volume + (AH: AvgVol + AHVol) + Mkt Cap + (optional
                // Exchange) + Sector + Watch
                // For AH/PM: 4 split price/% cols, 3 volume cols, no
                //   Exchange → 1+1+4+3+1+1+1 = 12
                // For daily: 2 price/% cols, 1 volume col, Exchange
                //   present → 1+1+2+1+1+1+1+1 = 9
                const cs = EXTENDED_HOURS_TYPES.has(type) ? 12 : 9;
                if (isLoading && !pageRows.length) {
                  return (
                    <tr>
                      <td colSpan={cs} className="px-4 py-12 text-center text-fg-subtle text-sm">
                        <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-slate-600" />
                        Loading…
                      </td>
                    </tr>
                  );
                }
                if (pageRows.length === 0) {
                  return (
                    <tr>
                      <td colSpan={cs} className="px-4 py-12 text-center text-fg-subtle text-sm">No results match your filters.</td>
                    </tr>
                  );
                }
                return pageRows.map((row) => (
                  <tr key={row.ticker} className="hover:bg-card-hover-bg transition-colors group">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        <TickerLink ticker={row.ticker} className="text-sky-400 hover:text-sky-300 text-sm font-semibold font-mono transition-colors">
                          {row.ticker}
                        </TickerLink>
                        {(() => {
                          const e = earningsByTicker.get(row.ticker.toUpperCase());
                          if (!e) return null;
                          return (
                            <span
                              title={e.isPast ? `Earnings ${e.date} (past)` : `Earnings ${e.date} (upcoming)`}
                              className={cn(
                                'inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold border',
                                e.isPast
                                  ? 'bg-slate-600/20 border-slate-500/40 text-slate-300'
                                  : 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300',
                              )}
                            >
                              E
                            </span>
                          );
                        })()}
                        {dilutingSet.has(row.ticker.toUpperCase()) && <DilutionBadge symbol={row.ticker} />}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">{row.name}</td>
                    {EXTENDED_HOURS_TYPES.has(type) ? (
                      <>
                        <td className="px-4 py-3 text-right text-sm text-white font-mono">
                          {row.regularClose != null ? formatCurrency(row.regularClose) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.dayChangePct != null ? (
                            <span className={cn('text-sm font-semibold font-mono', row.dayChangePct >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]')}>
                              {formatPct(row.dayChangePct)}
                            </span>
                          ) : <span className="text-fg-subtle text-sm">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-white font-mono">
                          {row.ahPrice != null ? formatCurrency(row.ahPrice) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {row.ahChangePct != null ? (
                            <span className={cn('text-sm font-semibold font-mono', row.ahChangePct >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]')}>
                              {formatPct(row.ahChangePct)}
                            </span>
                          ) : <span className="text-fg-subtle text-sm">—</span>}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-right text-sm text-white font-mono">{formatCurrency(row.price)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={cn('text-sm font-semibold font-mono', row.changePct >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]')}>
                            {formatPct(row.changePct)}
                          </span>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right text-sm text-fg-muted font-mono">{formatVolume(row.volume)}</td>
                    {EXTENDED_HOURS_TYPES.has(type) && (
                      <>
                        <td className="px-4 py-3 text-right text-sm text-fg-muted font-mono">
                          {row.avgVolume != null ? formatVolume(row.avgVolume) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-fg-muted font-mono">
                          {row.ahVolume != null ? formatVolume(row.ahVolume) : '—'}
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3 text-right text-sm text-fg-muted font-mono">{formatMarketCap(row.marketCap)}</td>
                    {!EXTENDED_HOURS_TYPES.has(type) && (
                      <td className="px-4 py-3 text-sm text-fg-subtle hidden md:table-cell">{row.exchange ?? '—'}</td>
                    )}
                    <td className="px-4 py-3 text-sm text-fg-subtle hidden lg:table-cell">{row.sector ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-fg-subtle hidden xl:table-cell">{row.industry || '—'}</td>
                    <td className="px-2 py-3">
                      <WatchButton ticker={row.ticker} />
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-default bg-card-hover-bg/30">
            <span className="text-xs text-fg-subtle">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg text-fg-muted hover:text-white hover:bg-border-default disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce<(number | '…')[]>((acc, p, i, arr) => {
                  if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '…'
                    ? <span key={`ellipsis-${i}`} className="px-2 text-slate-600 text-xs">…</span>
                    : <button
                        key={p}
                        onClick={() => setPage(p as number)}
                        className={cn(
                          'w-7 h-7 rounded-lg text-xs transition-colors',
                          currentPage === p ? 'bg-sky-500 text-white' : 'text-fg-muted hover:text-white hover:bg-border-default'
                        )}
                      >{p}</button>
                )}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg text-fg-muted hover:text-white hover:bg-border-default disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
    </TickerPopupProvider>
  );
}
